/**
 * Relational Intelligence LLM Service
 * 
 * Implements LLM prompts for:
 * - Thread → Relational Facts extraction
 * - Person → Relational Synthesis aggregation
 * - Relational Cognition → Scoring with evidence
 * - NBA (Next Best Action) generation
 * 
 * All outputs are strict JSON with message-id citations.
 * "Cognition developed by NAIBLE"
 */

import { blink } from '@/lib/blink';

// ============================================================================
// Type Definitions
// ============================================================================

export interface ThreadRelationalFacts {
  threadId: string;
  summary: string;  // ≤60 words
  theirGoals: string[];
  theirNeeds: string[];
  theirWants: string[];
  yourPromises: string[];
  openCommitments: Array<{
    who: 'them' | 'us';
    what: string;
    dueBy?: string;
  }>;
  asksOffers: {
    theyAsked: string[];
    theyOffered: string[];
    weAsked: string[];
    weOffered: string[];
  };
  sentiment: number;  // -1.0 to 1.0
  empathyMarkers: string[];
  enthusiasmMarkers: string[];
  preferenceCues: string[];
  pamssacSignals: {
    urgency: 'low' | 'medium' | 'high';
    budgetCues: 'approved' | 'seeking' | 'none' | 'unknown';
    decisionWindowDays?: number;
    roleClaims: Array<'economic_buyer' | 'champion' | 'user' | 'blocker' | 'unknown'>;
    pilotOpenness: 'yes' | 'no' | 'unknown';
    blockers: string[];
  };
  evidence: string[];  // Message IDs or snippets
}

export interface PersonRelationalSynthesis {
  personId: string;
  summary: string;  // ≤50 words
  goalsTop5: string[];
  needsTop5: string[];
  wantsTop5: string[];
  canHelpWithTop3: string[];
  communicationStyle: 'direct' | 'brief' | 'detailed' | 'collaborative' | 'reflective' | 'delegator';
  preferenceCues: string[];
  emotionalSignature: {
    avgSentiment: number;
    stability: 'steady' | 'volatile';
    empathyRate: number;
    enthusiasmRate: number;
  };
  reciprocityLedger: {
    gives: number;
    gets: number;
    introsMade: number;
    introsReceived: number;
  };
  lastContacted: string;
  evidenceThreads: string[];
}

export interface RelationalCognitionAnalysis {
  SAI: number;
  RH: number;
  EC: number;
  CIg: number;
  TD: number;
  PF: number;
  NL: number;
  RC: number;
  ER: number;
  DP: number;
  whyNow: string[];
}

export interface NextBestAction {
  action: string;
  channel: 'email' | 'slack' | 'call' | 'meeting';
  priority: 1 | 2 | 3;
  reason: string;
  message: string;  // 75 words max
  dueBy: string;
  guardrails: {
    noSendBefore?: string;
    respectWindowDays: number;
  };
}

// ============================================================================
// LLM Prompts
// ============================================================================

/**
 * System prompt for thread extraction
 */
const THREAD_EXTRACTION_SYSTEM_PROMPT = `You extract structured facts from a single conversation thread for relational intelligence. Prefer last 30 days. Output valid JSON only, matching the schema exactly. Cite message-ids or short quotes for non-obvious claims.

Cognition developed by NAIBLE.

Schema:
{
  "thread_id": "uuid",
  "summary": "≤60 words",
  "their_goals": ["..."],
  "their_needs": ["..."],
  "their_wants": ["..."],
  "your_promises": ["..."],
  "open_commitments": [{"who":"them|us","what":"...","due_by":"YYYY-MM-DD"}],
  "asks_offers": {"they_asked": ["..."], "they_offered": ["..."], "we_asked": ["..."], "we_offered": ["..."]},
  "sentiment": -1.0,
  "empathy_markers": ["appreciate","sorry for delay","congrats"],
  "enthusiasm_markers": ["excited","great news","can't wait"],
  "preference_cues": ["prefers_brief","evening_responder","assistant_schedules"],
  "pamssac_signals": {
    "urgency":"low|medium|high",
    "budget_cues":"approved|seeking|none|unknown",
    "decision_window_days": 14,
    "role_claims":["economic_buyer|champion|user|blocker|unknown"],
    "pilot_openness":"yes|no|unknown",
    "blockers":["security|legal|budget|none"]
  },
  "evidence": ["<msgid:...>", "\"90-sec reel + ROI by Friday\""]
}`;

/**
 * System prompt for person synthesis
 */
const PERSON_SYNTHESIS_SYSTEM_PROMPT = `Merge thread facts for one person. Keep recent, repeated facts. Output valid JSON.

Cognition developed by NAIBLE.

Schema:
{
  "person_id":"uuid",
  "summary":"≤50 words",
  "goals_top5":["..."],
  "needs_top5":["..."],
  "wants_top5":["..."],
  "can_help_with_top3":["..."],
  "communication_style":"direct|brief|detailed|collaborative|reflective|delegator",
  "preference_cues":["..."],
  "emotional_signature":{"avg_sentiment":0.3,"stability":"steady|volatile","empathy_rate":0.2,"enthusiasm_rate":0.3},
  "reciprocity_ledger":{"gives":5,"gets":7,"intros_made":2,"intros_received":1},
  "last_contacted":"YYYY-MM-DD",
  "evidence_threads":["t_91a2","t_77b1"]
}`;

/**
 * System prompt for RC scoring
 */
const RC_SCORING_SYSTEM_PROMPT = `Given the synthesis JSON + graph features + your goal topics, compute SAI/RH/EC/CIg/TD/PF/NL and RC/ER/DP. Output numbers and three "why now" bullets.

Cognition developed by NAIBLE.`;

/**
 * System prompt for NBA generation
 */
const NBA_GENERATION_SYSTEM_PROMPT = `Create one 2-minute action with message text that respects channel/style and closes the top open commitment. Don't oversell; be human.

Cognition developed by NAIBLE.

Output format:
{
  "action": "...",
  "channel": "email|slack|call|meeting",
  "priority": 1|2|3,
  "reason": "...",
  "message": "≤75 words",
  "due_by": "YYYY-MM-DD",
  "guardrails": {
    "no_send_before": "YYYY-MM-DD HH:MM",
    "respect_window_days": 7
  }
}`;

// ============================================================================
// LLM Service Functions
// ============================================================================

/**
 * Extract relational facts from a thread
 */
export async function extractThreadRelationalFacts(
  threadId: string,
  messages: Array<{
    id: string;
    subject?: string;
    bodyText?: string;
    bodyHtml?: string;
    senderEmail?: string;
    receivedDate?: string;
  }>
): Promise<ThreadRelationalFacts> {
  // Build thread content for LLM
  const threadContent = messages.map(msg => ({
    msgId: msg.id,
    date: msg.receivedDate,
    from: msg.senderEmail,
    subject: msg.subject,
    body: msg.bodyText || stripHtml(msg.bodyHtml || '')
  }));
  
  const prompt = `Extract relational facts from this email thread:

${JSON.stringify(threadContent, null, 2)}

Return valid JSON matching the schema.`;
  
  try {
    const result = await blink.ai.generateObject({
      model: 'gpt-4.1',
      prompt,
      schema: {
        type: 'object',
        properties: {
          thread_id: { type: 'string' },
          summary: { type: 'string' },
          their_goals: { type: 'array', items: { type: 'string' } },
          their_needs: { type: 'array', items: { type: 'string' } },
          their_wants: { type: 'array', items: { type: 'string' } },
          your_promises: { type: 'array', items: { type: 'string' } },
          open_commitments: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                who: { type: 'string' },
                what: { type: 'string' },
                due_by: { type: 'string' }
              }
            }
          },
          asks_offers: {
            type: 'object',
            properties: {
              they_asked: { type: 'array', items: { type: 'string' } },
              they_offered: { type: 'array', items: { type: 'string' } },
              we_asked: { type: 'array', items: { type: 'string' } },
              we_offered: { type: 'array', items: { type: 'string' } }
            }
          },
          sentiment: { type: 'number' },
          empathy_markers: { type: 'array', items: { type: 'string' } },
          enthusiasm_markers: { type: 'array', items: { type: 'string' } },
          preference_cues: { type: 'array', items: { type: 'string' } },
          pamssac_signals: {
            type: 'object',
            properties: {
              urgency: { type: 'string' },
              budget_cues: { type: 'string' },
              decision_window_days: { type: 'number' },
              role_claims: { type: 'array', items: { type: 'string' } },
              pilot_openness: { type: 'string' },
              blockers: { type: 'array', items: { type: 'string' } }
            }
          },
          evidence: { type: 'array', items: { type: 'string' } }
        }
      },
      systemPrompt: THREAD_EXTRACTION_SYSTEM_PROMPT
    });
    
    return {
      threadId: result.object.thread_id || threadId,
      summary: result.object.summary || '',
      theirGoals: result.object.their_goals || [],
      theirNeeds: result.object.their_needs || [],
      theirWants: result.object.their_wants || [],
      yourPromises: result.object.your_promises || [],
      openCommitments: result.object.open_commitments?.map((c: any) => ({
        who: c.who === 'them' ? 'them' : 'us',
        what: c.what,
        dueBy: c.due_by
      })) || [],
      asksOffers: {
        theyAsked: result.object.asks_offers?.they_asked || [],
        theyOffered: result.object.asks_offers?.they_offered || [],
        weAsked: result.object.asks_offers?.we_asked || [],
        weOffered: result.object.asks_offers?.we_offered || []
      },
      sentiment: result.object.sentiment || 0,
      empathyMarkers: result.object.empathy_markers || [],
      enthusiasmMarkers: result.object.enthusiasm_markers || [],
      preferenceCues: result.object.preference_cues || [],
      pamssacSignals: {
        urgency: result.object.pamssac_signals?.urgency || 'unknown',
        budgetCues: result.object.pamssac_signals?.budget_cues || 'unknown',
        decisionWindowDays: result.object.pamssac_signals?.decision_window_days,
        roleClaims: result.object.pamssac_signals?.role_claims || [],
        pilotOpenness: result.object.pamssac_signals?.pilot_openness || 'unknown',
        blockers: result.object.pamssac_signals?.blockers || []
      },
      evidence: result.object.evidence || []
    };
  } catch (error) {
    console.error('Failed to extract thread facts:', error);
    
    // Return default structure
    return {
      threadId,
      summary: 'Failed to analyze thread',
      theirGoals: [],
      theirNeeds: [],
      theirWants: [],
      yourPromises: [],
      openCommitments: [],
      asksOffers: {
        theyAsked: [],
        theyOffered: [],
        weAsked: [],
        weOffered: []
      },
      sentiment: 0,
      empathyMarkers: [],
      enthusiasmMarkers: [],
      preferenceCues: [],
      pamssacSignals: {
        urgency: 'unknown',
        budgetCues: 'unknown',
        roleClaims: [],
        pilotOpenness: 'unknown',
        blockers: []
      },
      evidence: []
    };
  }
}

/**
 * Synthesize person facts from multiple threads
 */
export async function synthesizePersonRelational(
  personId: string,
  threadFacts: ThreadRelationalFacts[]
): Promise<PersonRelationalSynthesis> {
  if (threadFacts.length === 0) {
    return {
      personId,
      summary: 'No thread data available',
      goalsTop5: [],
      needsTop5: [],
      wantsTop5: [],
      canHelpWithTop3: [],
      communicationStyle: 'direct',
      preferenceCues: [],
      emotionalSignature: {
        avgSentiment: 0,
        stability: 'steady',
        empathyRate: 0,
        enthusiasmRate: 0
      },
      reciprocityLedger: {
        gives: 0,
        gets: 0,
        introsMade: 0,
        introsReceived: 0
      },
      lastContacted: new Date().toISOString(),
      evidenceThreads: []
    };
  }
  
  const prompt = `Merge these thread facts for one person. Keep recent, repeated facts.

Thread facts:
${JSON.stringify(threadFacts, null, 2)}

Return valid JSON matching the schema.`;
  
  try {
    const result = await blink.ai.generateObject({
      model: 'gpt-4.1',
      prompt,
      schema: {
        type: 'object',
        properties: {
          person_id: { type: 'string' },
          summary: { type: 'string' },
          goals_top5: { type: 'array', items: { type: 'string' } },
          needs_top5: { type: 'array', items: { type: 'string' } },
          wants_top5: { type: 'array', items: { type: 'string' } },
          can_help_with_top3: { type: 'array', items: { type: 'string' } },
          communication_style: { type: 'string' },
          preference_cues: { type: 'array', items: { type: 'string' } },
          emotional_signature: {
            type: 'object',
            properties: {
              avg_sentiment: { type: 'number' },
              stability: { type: 'string' },
              empathy_rate: { type: 'number' },
              enthusiasm_rate: { type: 'number' }
            }
          },
          reciprocity_ledger: {
            type: 'object',
            properties: {
              gives: { type: 'number' },
              gets: { type: 'number' },
              intros_made: { type: 'number' },
              intros_received: { type: 'number' }
            }
          },
          last_contacted: { type: 'string' },
          evidence_threads: { type: 'array', items: { type: 'string' } }
        }
      },
      systemPrompt: PERSON_SYNTHESIS_SYSTEM_PROMPT
    });
    
    return {
      personId: result.object.person_id || personId,
      summary: result.object.summary || '',
      goalsTop5: result.object.goals_top5 || [],
      needsTop5: result.object.needs_top5 || [],
      wantsTop5: result.object.wants_top5 || [],
      canHelpWithTop3: result.object.can_help_with_top3 || [],
      communicationStyle: result.object.communication_style || 'direct',
      preferenceCues: result.object.preference_cues || [],
      emotionalSignature: {
        avgSentiment: result.object.emotional_signature?.avg_sentiment || 0,
        stability: result.object.emotional_signature?.stability || 'steady',
        empathyRate: result.object.emotional_signature?.empathy_rate || 0,
        enthusiasmRate: result.object.emotional_signature?.enthusiasm_rate || 0
      },
      reciprocityLedger: {
        gives: result.object.reciprocity_ledger?.gives || 0,
        gets: result.object.reciprocity_ledger?.gets || 0,
        introsMade: result.object.reciprocity_ledger?.intros_made || 0,
        introsReceived: result.object.reciprocity_ledger?.intros_received || 0
      },
      lastContacted: result.object.last_contacted || new Date().toISOString(),
      evidenceThreads: result.object.evidence_threads || []
    };
  } catch (error) {
    console.error('Failed to synthesize person:', error);
    
    // Fallback: manual aggregation
    const allGoals = new Set(threadFacts.flatMap(f => f.theirGoals));
    const allNeeds = new Set(threadFacts.flatMap(f => f.theirNeeds));
    const allWants = new Set(threadFacts.flatMap(f => f.theirWants));
    
    const avgSentiment = threadFacts.reduce((sum, f) => sum + f.sentiment, 0) / threadFacts.length;
    
    return {
      personId,
      summary: 'Aggregated from ' + threadFacts.length + ' threads',
      goalsTop5: Array.from(allGoals).slice(0, 5),
      needsTop5: Array.from(allNeeds).slice(0, 5),
      wantsTop5: Array.from(allWants).slice(0, 5),
      canHelpWithTop3: [],
      communicationStyle: 'direct',
      preferenceCues: [],
      emotionalSignature: {
        avgSentiment,
        stability: 'steady',
        empathyRate: 0,
        enthusiasmRate: 0
      },
      reciprocityLedger: {
        gives: 0,
        gets: 0,
        introsMade: 0,
        introsReceived: 0
      },
      lastContacted: new Date().toISOString(),
      evidenceThreads: threadFacts.map(f => f.threadId)
    };
  }
}

/**
 * Generate next best action
 */
export async function generateNextBestAction(
  contactName: string,
  synthesis: PersonRelationalSynthesis,
  rcScores: RelationalCognitionAnalysis,
  contextType: 'family' | 'friends' | 'mentors' | 'partners' | 'customers' = 'customers'
): Promise<NextBestAction> {
  const prompt = `Create one 2-minute action for ${contactName}.

Contact synthesis:
${JSON.stringify(synthesis, null, 2)}

RC Scores:
${JSON.stringify(rcScores, null, 2)}

Context: ${contextType}

Return valid JSON matching the schema. Message should be ≤75 words, human, not overselling.`;
  
  try {
    const result = await blink.ai.generateObject({
      model: 'gpt-4.1',
      prompt,
      schema: {
        type: 'object',
        properties: {
          action: { type: 'string' },
          channel: { type: 'string' },
          priority: { type: 'number' },
          reason: { type: 'string' },
          message: { type: 'string' },
          due_by: { type: 'string' },
          guardrails: {
            type: 'object',
            properties: {
              no_send_before: { type: 'string' },
              respect_window_days: { type: 'number' }
            }
          }
        }
      },
      systemPrompt: NBA_GENERATION_SYSTEM_PROMPT
    });
    
    return {
      action: result.object.action || 'Send check-in email',
      channel: (result.object.channel as any) || 'email',
      priority: (result.object.priority as any) || 2,
      reason: result.object.reason || 'Regular touchpoint',
      message: result.object.message || `Hi ${contactName}, wanted to follow up.`,
      dueBy: result.object.due_by || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      guardrails: {
        noSendBefore: result.object.guardrails?.no_send_before,
        respectWindowDays: result.object.guardrails?.respect_window_days || 7
      }
    };
  } catch (error) {
    console.error('Failed to generate NBA:', error);
    
    // Return default action
    const priority = rcScores.ER >= 80 ? 1 : rcScores.ER >= 60 ? 2 : 3;
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (priority === 1 ? 1 : priority === 2 ? 3 : 7));
    
    return {
      action: 'Send follow-up email',
      channel: 'email',
      priority,
      reason: 'Maintain relationship cadence',
      message: `Hi ${contactName}, hope you're doing well. Wanted to check in and see how things are going.`,
      dueBy: dueDate.toISOString(),
      guardrails: {
        respectWindowDays: 7
      }
    };
  }
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Strip HTML tags
 */
function stripHtml(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 2000); // Limit length
}
